import { useState } from "react";
import {
  Col,
  Form,
  Row,
  Select,
  Button,
  Space,
  Modal,
  notification,
  Skeleton,
  Typography,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { TenantRoleSelectOptions } from "admin/constants";
import { useRemoveTenantUserMutation, useTenantUserSelectOptions, useUpdateTenantUserRole } from "admin/queries";

export const UpdateUserRole = () => {
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationContextHolder] = notification.useNotification();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const roleOptions = TenantRoleSelectOptions();
  const { data: users, isLoading } = useTenantUserSelectOptions();
  const updateTenantUserRole = useUpdateTenantUserRole();
  const removeUser = useRemoveTenantUserMutation();

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    const user = users?.find((u) => u.value === userId);
    if (user) {
      form.setFieldsValue({ tenantRole: user.role || "" });
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUserId) return;
    try {
      await updateTenantUserRole.mutateAsync({
        userId: selectedUserId,
        role: form.getFieldValue("tenantRole"),
      });
      api.success({ duration: 3, message: "Role Updated" });
    } catch (error: any) {
      api.error({ message: "Error", description: error?.message });
    }
  };

  const handleDelete = () => {
    if (!selectedUserId) return;
    modal.confirm({
      title: "Remove User",
      icon: <ExclamationCircleOutlined />,
      content:
        "This removes the user's access to the tenant and all of its organizations. Their notes and history are kept.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeUser.mutateAsync(selectedUserId);
          setSelectedUserId(null);
          form.resetFields();
          api.success({ duration: 3, message: "User Removed" });
        } catch (error: any) {
          api.error({ message: "Error", description: error?.message });
        }
      },
    });
  };

  if (isLoading) return <Skeleton active />;

  return (
    <div className="update-user-role">
      {notificationContextHolder}
      {contextHolder}
      <Typography.Title level={5}>Update User Role</Typography.Title>
      <Typography.Paragraph type="secondary">
        Select a user to update their tenant role or remove them from the tenant.
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="user" label="Select User">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a user..."
                options={users?.map((u) => ({ value: u.value, label: u.label }))}
                onChange={handleUserSelect}
              />
            </Form.Item>
          </Col>
          {selectedUserId && (
            <Col span={8}>
              <Form.Item name="tenantRole" label="Tenant Role">
                <Select
                  options={[{ value: "", label: "Select..." }, ...roleOptions]}
                />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  loading={updateTenantUserRole.isPending}
                  onClick={handleUpdateRole}
                >
                  Update Role
                </Button>
                <Button
                  danger
                  loading={removeUser.isPending}
                  onClick={handleDelete}
                >
                  Remove User
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};
